import { Formik } from "formik";
import * as _ from "lodash";
import * as React from "react";
import { useMappedState } from "redux-react-hook";
import * as yup from "yup";
import styled from "styled-components";
import { toast } from "react-toastify";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { Button } from "src/library/components/Button";
import {
  Form,
  IFormField
} from "src/library/components/Forms";
import { ButtonLink } from "src/library/components/Link";
import DropInfoSidebar from "src/core/drops/components/DropInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  ERPSeedless,
  ERPRJL,
  InstalledModuleType,
  ERPUnity,
  ERPOdoo
} from "src/utils/constants";
import { intlPhoneRegex } from "src/utils/validation/regex";
import {
  useDropsApi,
  IDrop
} from "src/core/drops/api/drops.api";
import { ERPType } from "src/core/shared/api/globalSettings.api";
import type { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";
import { PageProps } from "src/pages/Router/routes";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import {
  IImportSetting,
  ImportOrigin,
  ImportSettingType,
  useImportSettingApi
} from "src/core/shared/api/importSetting.api";
import { validateHeaderMappingField } from "src/library/utils/validation";
import SelectedUser from "src/core/users/components/SelectedUser";

interface UrlParams {
  customerId: string;
  dropId: string;
}

const DropContactDetailsWrapper = styled.div``;

const updateContactDetailsForm: (
  terminology: ITerminology,
  drop: IDrop,
  erp: ERPType | null,
  isDisabled?: boolean,
  customDataHeaderMappings?: IImportSetting["headerMappings"],
  installedModules?: InstalledModuleType[]
) => IFormField[] = (terminology, drop, erp, isDisabled, customDataHeaderMappings, installedModules) => {
  // For disabling form fields
  const disabled = isDisabled || (erp !== ERPSeedless && erp !== ERPRJL);

  let fields: IFormField[] = [
    {
      type: "input",
      name: "dropName",
      label: `${terminology.drop.singular.text} Name`,
      placeholder: "Drop account name",
      value: drop.name,
      disabled,
      validation: yup
        .string()
        .min(1)
        .required("Drop Name is required")
    },
    {
      type: "input",
      name: "branchNumber",
      label: `${terminology.drop.singular.text} Number`,
      placeholder: "Branch number",
      value: drop.branchNumber,
      disabled,
      validation: yup
        .string()
        .min(1)
        .required("Branch Number is required")
    },
    {
      type: "fieldset",
      name: "address",
      label: "Address",
      fields: [
        {
          type: "input",
          name: "addressLine1",
          label: "Address line 1",
          placeholder: "Address line 1",
          value: drop.address.addressLine1,
          disabled,
          validation: yup.string().notRequired()
        },
        {
          type: "input",
          name: "addressLine2",
          label: "Address line 2",
          placeholder: "Address line 2",
          value: drop.address.addressLine2,
          disabled,
          validation: yup.string().notRequired()
        },
        {
          type: "input",
          name: "addressLine3",
          label: "Address line 3",
          placeholder: "Address line 3",
          value: drop.address.addressLine3,
          disabled,
          validation: yup.string().notRequired()
        },
        {
          type: "input",
          name: "city",
          label: "City",
          placeholder: "City",
          value: drop.address.city,
          disabled,
          validation: yup.string().notRequired()
        },
        {
          type: "input",
          name: "postcode",
          label: "Postcode",
          placeholder: "Postcode",
          value: drop.address.postcode,
          disabled,
          validation: yup.string().notRequired()
        }
      ]
    },
    {
      type: "input",
      name: "supplierBranchNumber",
      label: `Supplier ${terminology.drop.singular.text} Number`,
      placeholder: `Supplier ${terminology.drop.singular.text} Number`,
      value: drop.supplierBranchNumber ? drop.supplierBranchNumber : "",
      disabled
    },
    {
      type: "checkbox",
      name: "fulfilment",
      label: "",
      placeholder: "false",
      value: drop.fulfilment ? "true" : "false",
      options: [
        {
          text: terminology.fulfilment?.singular.text ?? "Pick & Dispatch",
          value: "true",
          checked: drop.fulfilment
        }
      ],
      disabled
    }

  ];

  if ((erp === ERPSeedless || erp === ERPRJL)) {
    fields = _.concat(fields, [
      {
        type: "input",
        name: "contactName",
        label: "Contact Name",
        placeholder: "Contact Name",
        value: drop.contactName,
        disabled,
        validation: yup.string().notRequired()
      },
      {
        type: "input",
        name: "contactNumber",
        label: "Contact Number",
        placeholder: "Contact Number",
        value: drop.contactNumber,
        disabled,
        validation: yup
          .string()
          .matches(intlPhoneRegex, "Please enter a valid telephone number")
          .notRequired()
      },
      {
        type: "input",
        name: "contactEmail",
        label: "Contact Email",
        placeholder: "Contact Email",
        value: drop.contactEmail,
        disabled,
        validation: yup
          .string()
          .email()
          .notRequired()
      }
    ]);
  }

  if (installedModules?.includes("support")) {
    fields = _.concat(fields, [
      {
        type: "input",
        name: "accountManager",
        label: "Account Manager",
        placeholder: "Account Manager Email",
        value: { ...drop.accountManager },
        Component: ({ setFieldValue, value }) => (
          <SelectedUser
            user={value && value.id ? value : null}
            updateUser={user => setFieldValue("accountManager", user || "")}
            terminology={terminology}
          />
        ),
        disabled,
        validation: yup
          .string()
          .notRequired()
      }
    ]);
  }

  if (customDataHeaderMappings?.length) {
    // The title that will separate custom input fields for normal fields
    const customDataTitle: IFormField = {
      type: "title",
      name: "Custom Fields",
      label: "Custom Fields",
      disabled
    };

    // Generate custom data fields
    const customDataInputs: IFormField[] = [];

    for (const headerItem of customDataHeaderMappings) {
      // Set default values
      let formFieldConfig: IFormField = {
        type: headerItem.formFieldType || "input",
        inputType: headerItem.type || "text",
        name: headerItem.external.replace(/\s/g, ""),
        label: headerItem.external,
        placeholder: _.find(drop.customData, { name: headerItem.external })?.description,
        value: _.find(drop.customData, { name: headerItem.external })?.value
      };

      if (headerItem.validation) {
        formFieldConfig.validation = validateHeaderMappingField(headerItem);
      }

      if (headerItem.options) {
        formFieldConfig = {
          ...formFieldConfig,
          options: headerItem.options.map(o => ({
            value: o,
            text: _.startCase(o)
          })),
          unselectedOption: {
            text: "Please select an option",
            value: ""
          }
        };
      }

      customDataInputs.push(formFieldConfig);
    }

    const customDataFields: IFormField[] = [ customDataTitle, ...customDataInputs ];

    fields = _.concat(fields, customDataFields);
  }

  return fields;
};

/**
 * Edit Drop contact details page
 */
const DropContactDetails: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const { match, terminology } = props;
  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const dropId = match ? parseInt(match.params.dropId, 0) : 0;
  const [ { isLoading: isLoadingDrop, drop }, { checkDropIsSynced } ] = useDrop(dropId);
  const formRef = React.useRef<Formik>(null);
  const [ updateDropResponse, updateDrop ] = useDropsApi("UPDATE_DROP");
  const checkPermission = useCheckUserPermission();
  const [ customerImportSettingRes, customerImportSettingReq ] = useImportSettingApi("GET_IMPORT_SETTING");

  const customDataHeaderMappings: IImportSetting["headerMappings"] = React.useMemo(() => {
    if (customerImportSettingRes.data) {
      return customerImportSettingRes.data.headerMappings.filter(item => item.custom);
    }

    return [];
  }, [ customerImportSettingRes.data ]);

  // generate list of custom data keys we'll use to sanatise data on update req
  const customDataFormKeys: string[] = React.useMemo(() => {
    if (drop?.customData) {
      return _.map(drop.customData, "name");
    }

    return [];
  }, [ drop ]);

  const canAmend = React.useMemo(() => {
    return checkPermission("amend-branch");
  }, [ checkPermission ]);

  const submitForm = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const { erp, installedModules } = useMappedState(({ global }: IStore) => ({
    erp: global.settings && global.settings.erp.name,
    installedModules: global?.settings?.installedModules || []
  }));

  const updateContactDetailsFormFields = React.useMemo(() => {
    if (isLoadingDrop || !drop) {
      return null;
    }

    return updateContactDetailsForm(terminology, drop, erp, !canAmend, customDataHeaderMappings, installedModules);
  }, [
    isLoadingDrop,
    drop,
    terminology,
    erp,
    canAmend,
    customDataHeaderMappings,
    installedModules
  ]);

  React.useEffect(() => {
    customerImportSettingReq({
      type: ImportSettingType.Customer,
      origin: ImportOrigin.CSV
    });
  }, [ customerImportSettingReq ]);

  // Update the drop state when on updateDropResponse
  React.useEffect(() => {
    if (updateDropResponse.data) {
      checkDropIsSynced();
      toast.success("Saved!");
    }

    if (updateDropResponse.error) {
      toast.error(updateDropResponse.error.message);
    }
  }, [ updateDropResponse.data, updateDropResponse.error ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <DropContactDetailsWrapper>
          {!isLoadingDrop && drop && updateContactDetailsFormFields?.length && (
            <Form
              ref={formRef}
              error={updateDropResponse.error ? updateDropResponse.error.message : null}
              submitting={updateDropResponse.pending}
              fields={updateContactDetailsFormFields}
              onSubmit={data => {
                let formData = Object.assign({}, data);

                // Structure the data to how we need to send it
                formData.name = formData.dropName;

                formData.address = {
                  addressLine1: formData.addressLine1,
                  addressLine2: formData.addressLine2,
                  addressLine3: formData.addressLine3,
                  city: formData.city,
                  postcode: formData.postcode
                };

                // format then omit custom data from formData if exists
                if (customDataFormKeys.length > 0) {
                  formData.customData = _.map(drop.customData, item => ({
                    ...item,
                    value: formData[ item.name ]
                  }));
                  formData = _.omit(formData, customDataFormKeys);
                }

                formData.accountManagerId = formData.accountManager?.id || null;

                if (formData.fulfilment.length) {
                  formData.fulfilment = true;
                } else {
                  formData.fulfilment = false;
                }

                // Remove incorrectly structured data
                delete formData.dropName;
                delete formData.addressLine1;
                delete formData.addressLine2;
                delete formData.addressLine3;
                delete formData.city;
                delete formData.postcode;

                updateDrop({
                  ...drop,
                  ...formData
                });
              }}
            />
          )}
        </DropContactDetailsWrapper>
      </MainContentWrapper>

      <InfoBarWrapper>
        <DropInfoSidebar
          terminology={terminology}
          isLoading={isLoadingDrop}
          data={drop}
          primaryAction={(erp === ERPSeedless || erp === ERPRJL) ? (
            <ButtonLink
              $buttonType="secondary"
              to={`/admin/customers/${customerId}/drops`}
            >
              Cancel
            </ButtonLink>
          ) : (
            undefined
          )}
          secondaryAction={(erp === ERPSeedless || erp === ERPRJL || erp === ERPUnity || erp === ERPOdoo) ? (
            <Button
              loading={updateDropResponse.pending}
              onClick={submitForm}
              disabled={!canAmend}
            >
              Save
            </Button>
          ) : (
            undefined
          )}
        />
      </InfoBarWrapper>
    </Layout>
  );
};

export default DropContactDetails;
